'use no memo';
import { MaintenanceQuestion, Reading, ReadingImage } from '@kalos/kalos-rpc';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  DataTable,
  DataTablePagination,
  Dialog,
  DialogContent,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import {
  EyeOpenIcon,
  GearIcon,
  ImageIcon,
  LockClosedIcon,
  Pencil1Icon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { type ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';

import { getLoadingColumns } from '../../../components/utils';
import { ROWS_PER_PAGE } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import {
  useMaintenanceQuestionCreateMutation,
  useMaintenanceQuestionDeleteMutation,
  useMaintenanceQuestionUpdateMutation,
  useReadingBatchGetQuery,
  useReadingCreateMutation,
  useReadingDeleteMutation,
  useReadingImageCreateMutation,
  useReadingUpdateMutation,
} from '../../../hooks/react-query/useReadingQuery';
import { useBatchUserQuery } from '../../../hooks/react-query/useUserQuery';
import { formatDate, MaintenanceQuestionClientService, timestamp } from '../../../tools/helpers';
import { getFieldMaskFromDirtyField } from '../EmployeeDepartments/utils';
import { ReadingImagesGallery } from './components/ReadingImageGallery/ReadingImageGallery';
import MaintenanceQuestionForm from './MaintenanceQuestionForm';
import ReadingForm from './ReadingForm';
import { getMaintenanceQuestionFormDefaultValues } from './utils';

const staticData = Array.from({ length: 3 }).map((_, i) => Reading.create({ id: i }));

/**
 * A reading dated before today is a record, not a form. Dates compare as YYYY-MM-DD strings
 * because every write path stores a local date via timestamp(true). No date fails open.
 */
const isPastReadingDate = (date: string) => {
  const day = date?.slice(0, 10);
  return !!day && day < timestamp(true);
};

/** NULL last_updated_date arrives as the zero datetime, so year <= 1 means never corrected. */
const correctionOf = (reading: Reading) => {
  const raw = reading.lastUpdatedDate ?? '';
  const year = Number(raw.slice(0, 4));
  if (!year || year <= 1) return undefined;
  return { date: raw, userId: reading.lastUpdatedByUserId || 0 };
};

const Readings = ({ serviceItemId, eventId }: { serviceItemId: number; eventId?: number }) => {
  const auth = useAuth();
  const loggedUserId = auth.user.id;

  const [editedMaintenanceQuestion, setEditedMaintenanceQuestion] = useState<MaintenanceQuestion>();

  console.log(editedMaintenanceQuestion);
  const [editedReading, setEditedReading] = useState<Reading>();
  const [readingUnlocked, setReadingUnlocked] = useState(false);

  const canCorrectReading = useMemo(
    () => auth.user.permissionGroups?.some((perm) => perm.name === 'ServiceCallAdmin') || false,
    [auth.user],
  );

  // Re-lock whenever the panel moves to a different reading.
  useEffect(() => {
    setReadingUnlocked(false);
  }, [editedReading?.id]);

  const editedReadingIsPast = !!editedReading?.id && isPastReadingDate(editedReading.date);
  // Past readings open locked; without a job context (no eventId) the panel is always a
  // viewer, since saving a reading requires an event to attach it to.
  const editedReadingViewOnly = (editedReadingIsPast && !readingUnlocked) || !eventId;

  const [maintenanceQuestions, setMaintenanceQuestions] = useState<{
    [key: number]: MaintenanceQuestion;
  }>({});

  const readingDeleteMutation = useReadingDeleteMutation();
  const readingCreateMutation = useReadingCreateMutation();
  const readingUpdateMutation = useReadingUpdateMutation();
  const maintenanceQuestionDeleteMutation = useMaintenanceQuestionDeleteMutation();
  const [page, setPage] = useState(0);

  const readingsQuery = useReadingBatchGetQuery({
    filter: {
      serviceItemId,
      pageNumber: page,

      orderBy: 'reading_date',
      orderDir: 'desc',
    },
  });
  const totalCount = readingsQuery.data?.totalCount || 0;

  const pageCount = useMemo(() => {
    return Math.ceil(totalCount / ROWS_PER_PAGE);
  }, [totalCount]);
  const maintenanceCreate = useMaintenanceQuestionCreateMutation();
  const maintenanceUpdate = useMaintenanceQuestionUpdateMutation();

  const usersQuery = useBatchUserQuery({
    filters: { overrideLimit: true, isEmployee: 1 },
  });

  const loadMaintenanceQuestions = async (readingIds: number[]) => {
    const maintenanceQuestions = await Promise.all(
      readingIds.map(async (id) => {
        const entry = MaintenanceQuestion.create();
        entry.readingId = id;

        try {
          return await MaintenanceQuestionClientService.Get(entry);
        } catch {
          console.log('FAIL');
          return null;
        }
      }),
    );
    return maintenanceQuestions.reduce(
      (aggr, entry) => ({
        ...aggr,
        ...(entry === null
          ? {}
          : {
              [entry.readingId]: entry,
            }),
      }),
      {},
    ) as {
      [key: number]: MaintenanceQuestion;
    };
  };

  const onDeleteClick = useCallback(
    (id: number): React.MouseEventHandler<HTMLButtonElement> =>
      async (e) => {
        e.stopPropagation();
        if (confirm('Are you sure you want to delete this reading?')) {
          maintenanceQuestionDeleteMutation.mutate(
            MaintenanceQuestion.create({
              readingId: id,
            }),
          );
          await readingDeleteMutation.mutateAsync(
            Reading.create({
              id,
            }),
          );
        }
      },
    [maintenanceQuestionDeleteMutation, readingDeleteMutation],
  );

  const handleOpenMaintenance = useCallback(async (readingId: number) => {
    const newQuestion = MaintenanceQuestion.create();
    newQuestion.readingId = readingId;
    const res = await MaintenanceQuestionClientService.Get(newQuestion);
    res.readingId = readingId;
    setEditedMaintenanceQuestion(res || newQuestion);
  }, []);

  const [viewReadingImagesId, setViewReadingImagesId] = useState<Reading['id'] | null>(null);

  const readingsTableCols: ColumnDef<Reading>[] = useMemo(
    () => [
      {
        id: 'date',
        header: 'Date',

        cell({ row }) {
          const user = usersQuery.data?.results.find((user) => user.id === row.original.userId);
          return [
            formatDate(row.original.date),
            '-',
            maintenanceQuestions[row.original.id] ? 'Maintenance' : 'Service',
            row.original.userId === 0 || (usersQuery.data?.results || []).length !== 0 || !user
              ? ''
              : ` - ${user.firstname} ${user.lastname}`,
          ].join(' ');
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-right">Actions</div>,
        cell({ row }) {
          return (
            <div className="flex justify-end gap-1">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipContent>Maintenance question</TooltipContent>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => {
                        handleOpenMaintenance(row.original.id);
                      }}
                    >
                      <GearIcon />
                    </Button>
                  </TooltipTrigger>
                </Tooltip>

                {row.original.readingImages && (
                  <Tooltip>
                    <TooltipContent>Images</TooltipContent>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => setViewReadingImagesId(row.original.id)}
                        size="icon"
                        variant="outline"
                      >
                        <ImageIcon />
                      </Button>
                    </TooltipTrigger>
                  </Tooltip>
                )}

                <Tooltip>
                  <TooltipContent>
                    {!eventId || isPastReadingDate(row.original.date)
                      ? 'View reading'
                      : 'Edit reading'}
                  </TooltipContent>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => setEditedReading(row.original)}
                    >
                      {!eventId || isPastReadingDate(row.original.date) ? (
                        <EyeOpenIcon />
                      ) : (
                        <Pencil1Icon />
                      )}
                    </Button>
                  </TooltipTrigger>
                </Tooltip>

                <Tooltip>
                  <TooltipContent side="left">Delete reading</TooltipContent>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" onClick={onDeleteClick(row.original.id)}>
                      <TrashIcon />
                    </Button>
                  </TooltipTrigger>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        },
      },
    ],
    [eventId, handleOpenMaintenance, maintenanceQuestions, onDeleteClick, usersQuery.data?.results],
  );

  const skeletonColumns = useMemo(() => getLoadingColumns(readingsTableCols), [readingsTableCols]);

  const table = useReactTable({
    data: !readingsQuery.isPending ? readingsQuery.data?.results || [] : staticData,
    columns: !readingsQuery.isFetching ? readingsTableCols : skeletonColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
  });

  const handleGetQuestion = useCallback(async (data: Reading[]) => {
    const maintenanceQuestions = await loadMaintenanceQuestions(data.map((id) => id.id));
    setMaintenanceQuestions(maintenanceQuestions);
  }, []);

  useEffect(() => {
    handleGetQuestion(readingsQuery.data?.results || []);
  }, [readingsQuery.isSuccess, readingsQuery.data?.results, handleGetQuestion]);

  const createReadingImageMutation = useReadingImageCreateMutation();

  const [isSavingReading, setIsSavingReading] = useState(false);

  const handleOnReadingSave = useCallback<
    Required<React.ComponentProps<typeof ReadingForm>>['onSave']
  >(
    async ({ data, dirtyFields }) => {
      if (editedReading && eventId) {
        setIsSavingReading(true);
        const req = Reading.create(data);
        const isNew = !editedReading.id;
        if (!isNew) {
          req.id = editedReading.id;
        }
        req.fieldMask = ['RefrigerantType'];

        req.eventId = eventId;
        req.serviceItemId = serviceItemId;
        req.date = timestamp(true);
        req.userId = loggedUserId;
        try {
          if (isNew) {
            const reading = await readingCreateMutation.mutateAsync(req);
            await Promise.allSettled(
              data.images.map((file) =>
                createReadingImageMutation.mutateAsync({
                  file,
                  req: ReadingImage.create({ readingId: reading.id }),
                }),
              ),
            );
          } else {
            const fieldMask = getFieldMaskFromDirtyField(dirtyFields);
            // Correcting a closed-day reading is attributable (save is only reachable
            // here after a ServiceCallAdmin unlocked the form).
            if (isPastReadingDate(editedReading.date)) {
              req.lastUpdatedByUserId = loggedUserId;
              req.lastUpdatedDate = timestamp();
              fieldMask.push('LastUpdatedByUserId', 'LastUpdatedDate');
            }
            req.fieldMask = fieldMask;
            await readingUpdateMutation.mutateAsync(req);
          }
        } finally {
          toast({
            variant: 'success',
            title: isNew ? 'Reading created' : 'Reading updated',
          });
          setIsSavingReading(false);
          setEditedReading(undefined);
        }
      }
    },
    [
      editedReading,
      eventId,
      serviceItemId,
      loggedUserId,
      readingCreateMutation,
      createReadingImageMutation,
      readingUpdateMutation,
    ],
  );

  const [isSavingMaintenance, setIsSavingMaintenance] = useState(false);
  const handleSaveMaintenance = useCallback<
    ComponentProps<typeof MaintenanceQuestionForm>['onSave']
  >(
    async ({ data, dirtyFields }) => {
      setIsSavingMaintenance(true);
      try {
        if (!editedMaintenanceQuestion)
          throw new Error('Unreachable path: No maintenance question to save');

        if (editedMaintenanceQuestion.id) {
          const updateRequest = MaintenanceQuestion.create({
            ...data,
            id: editedMaintenanceQuestion.id,
            fieldMask: getFieldMaskFromDirtyField(dirtyFields),
          });
          await maintenanceUpdate.mutateAsync(updateRequest);
        } else {
          const createRequest = MaintenanceQuestion.create({
            ...data,
            readingId: editedMaintenanceQuestion?.readingId,
          });
          await maintenanceCreate.mutateAsync(createRequest);
        }
        setEditedMaintenanceQuestion(undefined);
        toast({
          variant: 'success',
          title: 'Maintenance question saved',
        });
      } catch {
        toast({
          variant: 'destructive',
          title: 'Failed to save maintenance question',
        });
      } finally {
        setIsSavingMaintenance(false);
      }
    },
    [maintenanceCreate, maintenanceUpdate, editedMaintenanceQuestion],
  );

  const maintenanceQuestionFormDefaultValues = useMemo<
    ComponentProps<typeof MaintenanceQuestionForm>['defaultValues']
  >(() => {
    if (editedMaintenanceQuestion) {
      return getMaintenanceQuestionFormDefaultValues({ defaultValues: editedMaintenanceQuestion });
    }
    return getMaintenanceQuestionFormDefaultValues();
  }, [editedMaintenanceQuestion]);

  const viewedReading = useMemo(
    () => readingsQuery.data?.results.find((reading) => reading.id === viewReadingImagesId),
    [readingsQuery.data?.results, viewReadingImagesId],
  );
  const viewReadingImages = viewedReading?.readingImages?.results;

  return (
    <>
      <div className="h-full max-w-full overflow-auto">
        <div className="flex items-center justify-between px-3">
          <h3 className="pb-4 pt-3 text-xl font-semibold tracking-tight">Readings</h3>
          {(!!eventId || !!editedReading) && (
            <Button
              className="h-max"
              onClick={() => setEditedReading(!editedReading ? Reading.create() : undefined)}
            >
              {!editedReading ? 'Add Reading' : 'Close'}
            </Button>
          )}
        </div>
        {editedReading && (
          <div className="px-2 pb-4 md:px-4">
            {editedReadingIsPast && !readingUnlocked && (
              <Alert variant="info" className="mb-4">
                <AlertTitle className="flex items-center gap-2">
                  <LockClosedIcon />
                  This reading is closed
                </AlertTitle>
                <AlertDescription>
                  <p>
                    It was taken on {formatDate(editedReading.date)}, so it is a record now and
                    cannot be edited or given new photos.
                  </p>
                  {canCorrectReading && !!eventId && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => setReadingUnlocked(true)}
                      disabled={isSavingReading}
                    >
                      Correct this reading
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}
            {!!editedReading.id &&
              (() => {
                const correction = correctionOf(editedReading);
                const attributedUserId = correction?.userId || editedReading.userId || 0;
                const attributedUser = usersQuery.data?.results.find(
                  (user) => user.id === attributedUserId,
                );
                const name = attributedUser
                  ? ` by ${attributedUser.firstname} ${attributedUser.lastname}`.trimEnd()
                  : '';
                return (
                  <p className="mb-2 text-xs text-gray-500">
                    {correction ? 'Corrected' : 'Recorded'}{' '}
                    {formatDate(correction?.date || editedReading.date)}
                    {name}
                  </p>
                );
              })()}
            {/*
             * Remount on lock flip: ReadingForm feeds `disabled` into useForm, and flipping
             * that on a live form nulls the values.
             */}
            <ReadingForm
              key={`${editedReading.id}-${editedReadingViewOnly ? 'locked' : 'editable'}`}
              isLoading={isSavingReading}
              onSave={handleOnReadingSave}
              disabled={isSavingReading || editedReadingViewOnly}
              showImages={!editedReading.id}
              data={editedReading}
            />
          </div>
        )}
        <div className="flex items-center justify-between px-4 pb-4">
          <div className="flex items-center gap-2 overflow-auto">
            {readingsQuery.data && readingsQuery.data.totalCount > 25 && (
              <DataTablePagination
                key={readingsQuery.data?.totalCount}
                currentPage={page}
                pageCount={pageCount}
                setPage={setPage}
              />
            )}
          </div>
        </div>
        <div className="overflow-auto">
          <DataTable table={table} />
        </div>
      </div>

      {viewReadingImages && viewReadingImagesId && (
        <Dialog
          open={!!viewReadingImagesId}
          onOpenChange={(isOpen) => {
            if (!isOpen) setViewReadingImagesId(null);
          }}
        >
          <DialogContent className="h-[80vh] max-w-2xl overflow-auto p-0">
            <div className="px-3 pt-6 lg:px-6">
              <ReadingImagesGallery
                readingId={viewReadingImagesId}
                readingImages={viewReadingImages}
                showUploadControls={!isPastReadingDate(viewedReading?.date ?? '')}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
      {editedMaintenanceQuestion && (
        <Dialog open onOpenChange={(isOpen) => !isOpen && setEditedMaintenanceQuestion(undefined)}>
          <DialogContent className="max-h-svh overflow-auto">
            <h1 className="w-full pb-2 text-xl font-semibold tracking-tight">
              {editedMaintenanceQuestion?.id ? 'Edit' : 'Add'} Maintenance
            </h1>
            <MaintenanceQuestionForm
              disabled={isSavingMaintenance}
              isLoading={isSavingMaintenance}
              onSave={handleSaveMaintenance}
              defaultValues={maintenanceQuestionFormDefaultValues}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default Readings;
